import React, {useContext, useMemo, useRef} from "react";
import {inject, observer} from "mobx-react";
import { RootStore } from 'stores';
import _ from "lodash";
import {
    BaseButton, CoreFetch,
    DataTablePresenter, IntegrationApi,
    isTabletPortraitDown,
    ITableColumn, OAuthApi,
    ProviderIntegrationTerminal, ProviderIntegrationTerminalToken,
    Spinner,
    ToastContext, Variables
} from "@enter/core";
import * as S from "./shared.styles";
import {Icon} from "@mdi/react";
import {mdiArrowLeft, mdiPlus, mdiDelete, mdiRefresh, mdiRecycle, mdiCancel} from "@mdi/js";
import { DialogContext } from 'services';
import { SecretToggle, ConfirmDialog } from './components';
import styled from "@emotion/styled/macro";
import dateFns from "date-fns";
import {Box, Button, Typography} from "@mui/material";

const mdyFormat = (d: Date): string => dateFns.format(d, 'MM/DD/YYYY');
const parse = (d: string | undefined): Date => (d ? dateFns.parse(d) : new Date());


export const TableCenteredCell = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    flex-direction: column;
    text-align: center;
`;

export const SimpleIconButton = styled(BaseButton)`
    min-width: auto;
    margin-left: 20px;
    max-height: 30px;
    padding: 0;
    width: 150px;
    fill: ${Variables.Colors.ghostWhite};
    &:hover {
        fill: ${Variables.Colors.platinum};
    }
`;

export const SectionDivider = styled.div`
    width: 100%;
    height: 1px;
    border-bottom: 1px solid ${Variables.Colors.b300_l90};
`;


const terminalSettingsOutsideState = {
    loadedTerminalJavascript: false
};

export const TerminalSettings: React.FunctionComponent<{}> = inject('rootStore')(
    observer(
        (props: {}): React.ReactElement => {
            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const toastContext = useContext(ToastContext);
            const dialogContext = useContext(DialogContext);

            const { settingsStore } = rootStore;

            const initialTokens: Array<ProviderIntegrationTerminalToken> = [];

            const [terminalSettingState, setTerminalSettingState] = React.useState({
               loadingButton: false,
               loadingAddTerminalButton: false,
               terminalButtonText: 'Launch Terminal',
               tokens: initialTokens
            });


            async function reloadTokens() {
                if(settingsStore.integrationTerminals && settingsStore.integrationTerminals.length > 0 && settingsStore.integrationTerminals[0].id) {
                    const tokens: Array<ProviderIntegrationTerminalToken>
                        = await CoreFetch<IntegrationApi>(IntegrationApi).listProviderTerminalIntegrationTokens({id: settingsStore.integrationTerminals[0].id,
                    limit: 100, offset: 0
                });

                    setTerminalSettingState({
                        ...terminalSettingState,
                        loadingAddTerminalButton: false,
                        tokens
                    })

                    return tokens;
                }
            }

            React.useEffect(() => {
                if(settingsStore.integrationTerminals && settingsStore.integrationTerminals.length > 0 && settingsStore.integrationTerminals[0].id) {
                    reloadTokens().then((t) => {
                        console.log("Tokens refreshed in useEffect");
                    })
                }
            }, [settingsStore.integrationTerminals]);

            const columns: ITableColumn<ProviderIntegrationTerminalToken>[] = useMemo(
                () => [
                    {
                        id: 'Token',
                        Header: () => <S.TableHeader>Token</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => {
                            return <SecretToggle>{row.original.accessToken}</SecretToggle>
                        }
                    },
                    {
                        id: 'Created Date',
                        maxWidth: 150,
                        Header: () => <S.TableHeader>Created</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => { return <TableCenteredCell>{mdyFormat(parse(row.original.created))}</TableCenteredCell>}
                    },
                    {
                        id: 'Revoke',
                        maxWidth: 150,
                        Header: () => <S.TableHeader>Revoke</S.TableHeader>,
                        Cell: ({ row }): React.ReactElement => {

                            const handleRevokeToken = async () => {
                                if(row.original.accessToken) {
                                    const res = await CoreFetch<OAuthApi>(OAuthApi).oauthRevokeToken({oAuthRevokeTokenRequest: {token: row.original.accessToken}});
                                    toastContext.notify && toastContext.notify('Terminal token revoked');
                                }
                                dialogContext.hideDialog?.();
                            };

                            const handleModalOpen = () => {
                                dialogContext.showDialog?.({
                                    props: dialogProps,
                                    Component: <ConfirmDialog onSubmit={handleRevokeToken} onCancel={handleCancel} />,
                                });
                            }

                            const handleCancel = (): void => {
                                console.log("Cancel revoke token");
                                dialogContext.hideDialog?.();
                            };

                            const dialogProps = {
                                onClose: handleCancel,
                                disableBackdropClick: true,
                                disableEscapeKeyDown: true,
                                renderTitle: 'Are you sure you want to revoke this token?',
                            };

                            return <BaseButton variant="fab-light" size="large" onClick={handleModalOpen}>
                                <Icon path={mdiCancel} size={1} />
                            </BaseButton>;
                        }
                    }
                ],
                [],
            );

            const onClickAddTerminal = async (): Promise<void> => {
                await settingsStore.createTerminalToken();
                toastContext.notify && toastContext.notify('Terminal enabled');
            };

            const buildDataPresenter = (): DataTablePresenter<ProviderIntegrationTerminalToken> => {
                return new DataTablePresenter({
                    name: 'integrationTerminals',
                    data: terminalSettingState.tokens,
                    columns,
                    filterSchemas: [],
                })
            }

            const renderEnableTerminalButton = () => {
                return <Box>
                    <Typography variant="subtitle2" style={{paddingBottom: '10px'}}>Click below to enable the terminal interface on your account</Typography>
                    <S.SubCategoryAddButton variant="primary" onClick={onClickAddTerminal}>
                    <S.SubCategoryPlus path={mdiPlus} size={1} /> Enable Terminal
                </S.SubCategoryAddButton>
                </Box>;
            }


            const onClickAddToken = async (): Promise<void> => {
                setTerminalSettingState({
                    ...terminalSettingState,
                    loadingAddTerminalButton: true
                });
                if(settingsStore.integrationTerminals && settingsStore.integrationTerminals.length > 0 && settingsStore.integrationTerminals[0].id) {
                    const res = await CoreFetch<OAuthApi>(OAuthApi).oauthStandardToken({
                        oAuthStandardTokenRequest: {
                            grantType: 'client_credentials',
                            clientId: settingsStore.integrationTerminals[0].clientId,
                            clientSecret: settingsStore.integrationTerminals[0].clientSecret
                        }
                    })
                    toastContext.notify && toastContext.notify('Terminal token created');
                    await reloadTokens();
                }
            };

            const renderAddTokenButton = () => {
                return <SimpleIconButton size="small" onClick={onClickAddToken}>
                    <Icon path={mdiPlus} style={{height: '20px'}}/>
                    Add Token
                </SimpleIconButton>
            }



            const renderRecycleButton = () => {
                const handleDelete = async () => {
                    if(settingsStore.integrationTerminals && settingsStore.integrationTerminals.length > 0 && settingsStore.integrationTerminals[0].id) {
                        await settingsStore.deleteTerminalToken(settingsStore.integrationTerminals[0].id);
                        await settingsStore.createTerminalToken();
                        toastContext.notify && toastContext.notify('Terminal API credentials cycled');
                    }
                    dialogContext.hideDialog?.();
                };

                const handleModalOpen = () => {
                    dialogContext.showDialog?.({
                        props: dialogProps,
                        Component: <ConfirmDialog onSubmit={handleDelete} onCancel={handleCancel} />,
                    });
                }

                const handleCancel = (): void => {
                    console.log("Cancel delete Integration");
                    dialogContext.hideDialog?.();
                };

                const dialogProps = {
                    onClose: handleCancel,
                    disableBackdropClick: true,
                    disableEscapeKeyDown: true,
                    renderTitle: 'Are you sure you want to reset terminal API credentials? This will disable all active tokens.',
                };

                return <S.SubCategoryAddButton variant="primary" onClick={handleModalOpen}>
                    <S.SubCategoryPlus path={mdiRecycle} size={1} /> Reset API Credentials
                </S.SubCategoryAddButton>;
            }

            const launchTerminal = () => {
                const isSandbox = /sandbox/.test(process.env.REACT_APP_API_URL || '');
                const terminalLink = isSandbox ? 'https://terminal.sandbox.enter.health' : 'https://terminal.enter.health';

                setTerminalSettingState({
                    ...terminalSettingState,
                    loadingButton: true,
                    terminalButtonText: ''
                });

                function clearOutButtons() {
                    const buttons = document.getElementsByTagName('button');
                    for (let i = 0; i < buttons.length; i++) {
                        if (buttons[i].dataset.payment == 'enter') {
                            buttons[i].innerHTML = "";
                        }
                    }
                }

                if(!terminalSettingsOutsideState.loadedTerminalJavascript) {
                    const script = document.createElement("script");
                    script.id = "enter-payments-script";
                    script.src = terminalLink + '/terminal.min.js';
                    script.async = true;

                    document.body.appendChild(script);

                    setTimeout(() => {
                        if(window.Terminal) {
                            clearOutButtons();
                            window.Terminal.initialize({
                                token: terminalSettingState.tokens[0].accessToken
                            });
                        }
                    }, 2000);
                } else {
                    if(window.Terminal) {
                        clearOutButtons();
                        window.Terminal.initialize({
                            token: terminalSettingState.tokens[0].accessToken
                        });
                    }
                }
            }

            console.log("settingsStore.integrationTerminals=", settingsStore.integrationTerminals);

            return (settingsStore.integrationTerminals === undefined || settingsStore.integrationTerminals === null) ? <Spinner/> : <S.SubCategoryCol>
                <S.SubCategoryItem key="members">
                    <S.SubCategoryItemRow>
                        <S.SubCategoryH1>
                            {isTabletPortraitDown() && (
                                <S.ReturnLink to="/settings">
                                    {(): React.ReactElement => <Icon path={mdiArrowLeft} size={1} />}
                                </S.ReturnLink>
                            )}
                            Terminal Setup
                        </S.SubCategoryH1>
                    </S.SubCategoryItemRow>
                    <Box style={{display: 'flex', flexDirection: 'column', width: '100%', paddingBottom: '20px'}}>
                        {(settingsStore.integrationTerminals.length == 0) ? renderEnableTerminalButton() :
                            <Box style={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                                <Box style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
                                    <Typography variant="subtitle2">Terminal API Client Id: </Typography>
                                    <Typography variant="subtitle2"> {(settingsStore.integrationTerminals[0].clientId)}</Typography>
                                </Box>
                                <Box style={{display: 'flex', flexDirection: 'row', width: '100%', paddingBottom: '5px'}}>
                                    <Typography variant="subtitle2">Terminal API Client Secret: </Typography>
                                    <Box style={{display: 'flex', flex: '1 0 auto', minWidth: '150px', maxWidth: '350px', position: 'relative', overflowY: 'auto', overflowX: 'auto'}}>
                                        <Typography variant="subtitle2" style={{boxSizing: 'border-box', flex: '100 0 auto', minWidth: '50px', maxWidth: '400px', width: '100px'}}>
                                            <SecretToggle>{(settingsStore.integrationTerminals[0].clientSecret)}</SecretToggle>
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    {renderRecycleButton()}
                                </Box>
                            </Box>
                        }
                    </Box>
                    {settingsStore.integrationTerminals.length > 0 ? <SectionDivider/> : <span></span> }
                    {settingsStore.integrationTerminals.length > 0 ?
                        <Box style={{display: 'flex', flexDirection: 'row', width: '100%', marginTop: '40px'}}>
                            <Typography variant="h6">Terminal Tokens</Typography>
                            {renderAddTokenButton()}
                        </Box> : <span></span>
                    }

                    {terminalSettingState.tokens && terminalSettingState.tokens.length > 0 ?
                        <S.Table presenter={buildDataPresenter()} hideFiltering={true} hidePagination={true} hideSettings={true} />
                            : <span></span>
                    }
                </S.SubCategoryItem>
            </S.SubCategoryCol>;
        },
    ),
);